import type { TVenueType } from '@lucimark/shared/constants/venues'
import type { TPortalPlayerTierPricingMode } from '@lucimark/shared/database/custom-types/portal-player-tier-pricing-mode'
import type { TPortalLoopSettingsByAspectRatio } from '@lucimark/shared/helpers/portalLoopSettingsByAspectRatio'
import type { TPortalTaxConfig } from '@lucimark/shared/models/marketplace-catalog'
import type { TPlaylistEmbedPlayback } from '@lucimark/shared/models/playlist-embed-playback'
/**
 * Tenant DO SQLite tables for Lucimark Book (advertiser portal fulfillment).
 */
import { VENUE_TYPES } from '@lucimark/shared/constants/venues'
import { portalPlayerTierPricingModeType } from '@lucimark/shared/database/custom-types/portal-player-tier-pricing-mode'
import { index, int, real, sqliteTable, text, unique } from 'drizzle-orm/sqlite-core'

/** @deprecated Prefer {@link VENUE_TYPES} from `@lucimark/shared/constants/venues`. */
export const PORTAL_VENUE_TYPES = VENUE_TYPES
export type TPortalVenueType = TVenueType

/** Per-tenant marketplace / Book settings (single row per tenant DO). */
export const portalBillingSettings = sqliteTable('portal_billing_settings', {
  tenantId: text('tenant_id').primaryKey(),
  portalListedOnNetwork: int('portal_listed_on_network', { mode: 'boolean' }).notNull().default(false),
  currency: text().notNull().default('USD'),
  loopCapacity: int('loop_capacity').notNull().default(6),
  slotDurationSeconds: int('slot_duration_seconds').notNull().default(10),
  slotPricePerDayMinor: int('slot_price_per_day_minor').notNull().default(0),
  taxPercent: real('tax_percent').notNull().default(0),
  taxConfigJson: text('tax_config_json', { mode: 'json' }).$type<TPortalTaxConfig | null>(),
  termsUrl: text('terms_url'),
  reviewSlaHours: int('review_sla_hours'),
  reviewNotificationEmail: text('review_notification_email'),
  houseContentPlaylistUrn: text('house_content_playlist_urn'),
  houseContentPlaylistEmbedPlaybackJson: text('house_content_playlist_embed_playback_json', { mode: 'json' })
    .$type<TPlaylistEmbedPlayback | null>(),
  tailFillerAssetUrn: text('tail_filler_asset_urn'),
  loopSettingsByAspectRatioJson: text('loop_settings_by_aspect_ratio_json', { mode: 'json' })
    .$type<TPortalLoopSettingsByAspectRatio>(),
  /** Caption under the booking QR on portal-bookable player idle screens; null = platform default. */
  playerBookingQrCta: text('player_booking_qr_cta'),
  /** When false, portal-bookable screens omit the Book QR from the idle screensaver. */
  playerBookingQrEnabled: int('player_booking_qr_enabled', { mode: 'boolean' }).notNull().default(true),
  /** Locale for the Lucimark Book advertiser storefront (`en` | `ptBR`). */
  advertiserLocale: text('advertiser_locale').notNull().default('en'),
  /** @deprecated Unused; global fee lives on `platform_billing_settings.marketplace_platform_fee_percent`. */
  platformFeePercent: real('platform_fee_percent'),
  updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date())
})

export const playerPortalListings = sqliteTable('player_portal_listing', {
  playerId: text('player_id').primaryKey(),
  venueName: text('venue_name'),
  /** @deprecated Legacy slug; prefer {@link venueTypeId}. */
  venueType: text('venue_type', { enum: PORTAL_VENUE_TYPES }),
  /** Platform catalog reference (`venue_type.id`). Required to publish once map ships. */
  venueTypeId: text('venue_type_id'),
  addressLine: text('address_line'),
  city: text(),
  placementNotes: text('placement_notes'),
  audienceDescription: text('audience_description'),
  contentRestrictions: text('content_restrictions'),
  operatingHoursJson: text('operating_hours_json', { mode: 'json' }),
  sharePreciseLocation: int('share_precise_location', { mode: 'boolean' }).notNull().default(false),
  /** Operator-picked map pin (Book discovery). When set, overrides centroid fallback. */
  listingLatitude: real('listing_latitude'),
  listingLongitude: real('listing_longitude'),
  cityCentroidLat: real('city_centroid_lat'),
  cityCentroidLng: real('city_centroid_lng'),
  updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date())
})

export const portalPlayerTiers = sqliteTable('portal_player_tier', {
  id: text().primaryKey(),
  name: text().notNull(),
  description: text(),
  sortOrder: int('sort_order').notNull().default(0),
  isDefault: int('is_default', { mode: 'boolean' }).notNull().default(false),
  pricingMode: portalPlayerTierPricingModeType().notNull().default('flat_per_day'),
  flatSlotPricePerDayMinor: int('flat_slot_price_per_day_minor'),
  updatedAt: int('updated_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date())
}, t => [
  index('portal_player_tier_sort_order_idx').on(t.sortOrder)
])

export const portalPlayerTierPriceWindows = sqliteTable('portal_player_tier_price_window', {
  id: text().primaryKey(),
  tierId: text('tier_id').notNull().references(() => portalPlayerTiers.id, { onDelete: 'cascade' }),
  daysJson: text('days_json', { mode: 'json' }).$type<number[]>().notNull(),
  startHour: int('start_hour').notNull(),
  startMinute: int('start_minute').notNull(),
  endHour: int('end_hour').notNull(),
  endMinute: int('end_minute').notNull(),
  slotPricePerDayMinor: int('slot_price_per_day_minor').notNull(),
  sortOrder: int('sort_order').notNull().default(0)
}, t => [
  index('portal_player_tier_price_window_tier_id_idx').on(t.tierId)
])

export const playerPortalPhotos = sqliteTable('player_portal_photo', {
  id: text().primaryKey(),
  playerId: text('player_id').notNull(),
  assetUrn: text('asset_urn').notNull(),
  sortOrder: int('sort_order').notNull().default(0),
  caption: text(),
  createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date())
}, t => [
  unique('player_portal_photo_player_sort_unique').on(t.playerId, t.sortOrder),
  index('player_portal_photo_player_id_idx').on(t.playerId)
])

/** Tracks tenant materialized copies of marketplace creatives for GC on teardown. */
export const portalAdOrderMaterializedAssets = sqliteTable('portal_ad_order_materialized_asset', {
  id: text().primaryKey(),
  portalAdOrderId: text('portal_ad_order_id').notNull(),
  assetUrn: text('asset_urn').notNull(),
  advertiserCreativeId: text('advertiser_creative_id').notNull(),
  createdAt: int('created_at', { mode: 'timestamp' }).notNull().$defaultFn(() => new Date())
}, t => [
  index('portal_ad_order_materialized_asset_order_idx').on(t.portalAdOrderId),
  unique('portal_ad_order_materialized_asset_order_asset_unique').on(t.portalAdOrderId, t.assetUrn)
])
